import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Divider, Typography } from "@mui/material";
import { computeNiceAxis } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/niceScale";
import TextWithCopy from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import { encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { useSOHBuckets } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/sohBuckets";
import { usePagesContent } from "../../Hooks/usePagesContent";
import { toTitleCase } from "../../Helper/Formatters/TextFormatter";
import { formatKmAxisTick, KM_X_AXIS_KEYS } from "../../Helper/Formatters/NumberFormatter";
import { changeDateFormat } from "../../Helper/DatePicker/DateConverter";
import {
  buildPlotGroups,
  buildScatterSeries,
  buildScatterColors,
  buildScatterMarkers,
  collectAxisValues,
  resolveYAxisValues,
  SOH_Y_FALLBACK,
  isSOHPlottable,
  toSOHPoint,
  kmAxisMinStep,
  buildLegendLabel,
  getLegendSwatchSx,
} from "./scatterBucketUtils";
import { UNAVAILABLE_BUCKET_COLOR } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/unavailableBucket";

function formatDaysAge(days) {
  if (days == null || isNaN(days)) return "-";
  const d = Math.round(days);
  if (d < 30) return `${d} day${d !== 1 ? "s" : ""}`;
  const years  = Math.floor(d / 365);
  const months = Math.floor((d % 365) / 30);
  if (years === 0) return `${months} month${months !== 1 ? "s" : ""}`;
  if (months === 0) return `${years} year${years !== 1 ? "s" : ""}`;
  return `${years} yr${years !== 1 ? "s" : ""}, ${months} mo`;
}

function smartTooltipPos(clientX, clientY, tooltipW = 220, tooltipH = 170) {
  const M  = 14;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = clientX + M;
  let top  = clientY + M;
  if (left + tooltipW > vw - 8) left = clientX - tooltipW - M;
  if (top  + tooltipH > vh - 8) top  = clientY - tooltipH - M;
  return { left: Math.max(8, left), top: Math.max(8, top) };
}

const TOOLTIP_FIELD_RENDERERS = {
  soh:       { label: "SOH",          render: (b) => b.soh       != null ? `${Number(b.soh).toFixed(2)}%`                        : "-" },
  age:       { label: "Age",          render: (b) => b.ageDays   != null ? formatDaysAge(b.ageDays)                              : "-" },
  bmsCycles: { label: "Battery Cycle",render: (b) => b.bmsCycles != null ? Math.round(b.bmsCycles).toLocaleString()              : "-" },
  odometer:  { label: "Odometer",     render: (b) => b.odometer  != null ? formatKmAxisTick(b.odometer)       : "-" },
  latestSOHPredictionOdometer: { label: "Odometer", render: (b) => b.latestSOHPredictionOdometer != null ? formatKmAxisTick(b.latestSOHPredictionOdometer) : "-" },
  sohConfidence: { label: "SOH Confidence", render: (b) => b.sohConfidence != null && b.sohConfidence !== "" ? toTitleCase(b.sohConfidence) : "-" },
  sohComputedDate: { label: "Last SOH Computed", render: (b) => {
    if (!b.sohComputedDate) return "-";
    const formatted = changeDateFormat(b.sohComputedDate, "DD MMM YYYY");
    return formatted && formatted !== "Invalid date" ? formatted : "-";
  } },
};

/**
 * Shared SOH scatter chart — legend (optional) + chart + x-filter pills + hover tooltip.
 * Dot click always opens battery health in a new tab.
 *
 * Props
 *   batteriesByVisibleCategory  [{category, batteries[]}] — filtered for selection
 *   selectedCategories          Set<string>
 *   onCategorySelect            callback(name)
 *   activeXFilter               "age" | "bmsCycles" | "odometer"
 *   onXFilterChange             callback(key)
 *   xFilters                    [{key, label}]
 *   chartId                     unique ApexCharts chart ID string
 *   height                      chart height px (default 360)
 *   showLegend                  render the legend (default true)
 *   isVehicle                   affects tooltip label (default false)
 *   tooltipFields               array of field keys to show in hover tooltip (from org meta)
 *   tooltipIdentifier           { key, label } for the tooltip identifier row (from org meta)
 *   unavailableCount            optional — total "SOH Unavailable" batteries for the legend
 *                               count; defaults to the visible unavailable group's size
 *
 * The "SOH Unavailable" group (category.isUnavailable) is not drawn: a few thousand grey
 * dots at (0, 0) cluttered the chart. Its legend entry and count stay, and the donut
 * still has its slice.
 */
const SOHScatterChart = ({
  batteriesByVisibleCategory = [],
  selectedCategories,
  onCategorySelect,
  activeXFilter,
  onXFilterChange,
  xFilters = [],
  chartId,
  height = 360,
  showLegend = true,
  isVehicle = false,
  tooltipFields = null,
  tooltipIdentifier = null,
  unavailableCount,
}) => {
  const sohBuckets = useSOHBuckets();
  const pageContent = usePagesContent();
  const tabsToShow = pageContent?.metaInfo?.digitalTwin?.tabsToShow || [];
  const healthTabValue = tabsToShow.includes('batteryHealth')
    ? 'batteryHealth'
    : tabsToShow.includes('health')
    ? 'health'
    : 'overview';

  const [scatterHover, setScatterHover] = useState(null);
  const visibleGroupsRef    = useRef([]);
  const scatterHideTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(scatterHideTimerRef.current), []);

  // Clear hover when selection or x-filter changes
  useEffect(() => { setScatterHover(null); }, [selectedCategories, activeXFilter]);

  // Range buckets keep batteries with a real SOH; the unavailable bucket is not plotted,
  // only counted for the legend. Series, colours, markers, axes and the hover/click index
  // lookups (visibleGroupsRef) all use plottedGroups, so indexes stay aligned.
  const { plottedGroups, unavailableTotal } = useMemo(
    () => buildPlotGroups(batteriesByVisibleCategory, { isPlottable: isSOHPlottable, plotUnavailable: false }),
    [batteriesByVisibleCategory]
  );
  const legendUnavailableCount = Number.isFinite(unavailableCount) ? unavailableCount : unavailableTotal;

  useEffect(() => {
    visibleGroupsRef.current = plottedGroups;
  }, [plottedGroups]);

  const scatterSeries = useMemo(
    () => buildScatterSeries(plottedGroups, (b, category) => toSOHPoint(b, activeXFilter, category)),
    [plottedGroups, activeXFilter]
  );

  const xAxisTitle = useMemo(() => {
    // org-meta override (axisCombinations[].label) takes precedence over the defaults
    const configured = xFilters.find((f) => f.key === activeXFilter)?.axisLabel;
    if (configured) return configured;
    if (activeXFilter === "age") return "Age (Months)";
    if (activeXFilter === "bmsCycles") return "BMS Cycles";
    if (activeXFilter === "odometer") return "Odometer (km)";
    if (activeXFilter === "latestSOHPredictionOdometer") return "Odometer (km)";
    return "";
  }, [activeXFilter, xFilters]);

  // x spans every drawn point, so it includes 0 when a point has no x value. y comes from
  // real SOH values, or 0..100 when there is no real SOH at all. minStep keeps the rounded tick labels distinct; nonNegative turns an
  // all-zero x axis into 0..5 instead of -1..1.
  const { xAxis, yAxis } = useMemo(() => {
    const { xValues } = collectAxisValues(scatterSeries);
    const yValues = resolveYAxisValues(scatterSeries, plottedGroups, SOH_Y_FALLBACK);
    const xMinStep = KM_X_AXIS_KEYS.has(activeXFilter) ? kmAxisMinStep(xValues) : 1;
    return {
      xAxis: computeNiceAxis(xValues.length ? xValues : [0], 6, { minStep: xMinStep, nonNegative: true }),
      yAxis: computeNiceAxis(yValues, 6, { minStep: 1, nonNegative: true }),
    };
  }, [scatterSeries, plottedGroups, activeXFilter]);

  const scatterColors = useMemo(
    () => buildScatterColors(plottedGroups, UNAVAILABLE_BUCKET_COLOR),
    [plottedGroups]
  );
  const scatterMarkers = useMemo(() => buildScatterMarkers(plottedGroups), [plottedGroups]);

  // Exactly the nice ticks: forcing more intervals than the nice scale has produced
  // fractional ticks that rounded to duplicate labels ("0 km, 0 km, 1 km, 1 km").
  const xTickAmount = xAxis.ticks.length - 1;
  const yTickAmount = yAxis.ticks.length - 1;

  useEffect(() => {
    try {
      ApexCharts.exec(chartId, "updateOptions", {
        yaxis: {
          min:            yAxis.min,
          max:            yAxis.max,
          tickAmount:     yTickAmount,
          forceNiceScale: false,
          title: {
            text:    "SOH (%)",
            rotate:  -90,
            offsetX: 4,
            style:   { fontSize: "11px", fontWeight: 400, color: "#8F8F8F" },
          },
          labels: {
            formatter: (val) => Math.round(val),
            offsetX:   -8,
            style:     { fontSize: "11px", colors: "#8F8F8F" },
          },
        },
      }, false, false);
    } catch (_) {}
  }, [chartId, yAxis.min, yAxis.max, yTickAmount]);

  const handleScatterMouseEnter = useCallback((event, _ctx, config) => {
    clearTimeout(scatterHideTimerRef.current);
    const { seriesIndex, dataPointIndex } = config;
    if (seriesIndex < 0 || dataPointIndex < 0) return;
    const group   = visibleGroupsRef.current[seriesIndex];
    const battery = group?.batteries[dataPointIndex];
    if (!battery) return;
    setScatterHover({
      clientX:       event.clientX,
      clientY:       event.clientY,
      battery,
      color:         group.category.color,
      category:      group.category.displayLabel ?? group.category.name,
      isUnavailable: Boolean(group.category.isUnavailable),
    });
  }, []);

  const handleScatterMouseLeave = useCallback(() => {
    scatterHideTimerRef.current = setTimeout(() => setScatterHover(null), 400);
  }, []);

  const openBattery = useCallback((battery) => {
    if (!battery?.batteryID) return;
    const hash = encryptTheParams({ batteryID: battery.batteryID, tabValue: healthTabValue });
    window.open(`/specificBattery?${hash}`, "_blank", "noreferrer");
  }, [healthTabValue]);

  const handleScatterDotClick = useCallback((_event, _ctx, config) => {
    const { seriesIndex, dataPointIndex } = config;
    if (seriesIndex < 0 || dataPointIndex < 0) return;
    const group   = visibleGroupsRef.current[seriesIndex];
    const battery = group?.batteries[dataPointIndex];
    openBattery(battery);
  }, [openBattery]);

  const scatterOptions = useMemo(() => ({
    chart: {
      id:   chartId,
      type: "scatter",
      toolbar: {
        show: true,
        tools: {
          download:  false,
          selection: false,
          zoom:      true,
          zoomin:    true,
          zoomout:   true,
          pan:       true,
          reset:     true,
        },
        autoSelected: "zoom",
      },
      animations: { enabled: false },
      zoom: { enabled: true, type: "x", allowMouseWheelZoom: false },
      events: {
        dataPointMouseEnter: handleScatterMouseEnter,
        dataPointMouseLeave: handleScatterMouseLeave,
        dataPointSelection:  handleScatterDotClick,
      },
    },
    colors:     scatterColors,
    legend:     { show: false },
    dataLabels: { enabled: false },
    xaxis: {
      type:       "numeric",
      min:        xAxis.min,
      max:        xAxis.max,
      tickAmount: xTickAmount,
      title: { text: xAxisTitle, style: { fontSize: "11px", fontWeight: 400, color: "#8F8F8F" } },
      labels: {
        formatter: (val) => (KM_X_AXIS_KEYS.has(activeXFilter) ? formatKmAxisTick(val) : Math.round(val)),
        style:     { fontSize: "11px", colors: "#8F8F8F" },
      },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis: {
      min:            yAxis.min,
      max:            yAxis.max,
      tickAmount:     yTickAmount,
      forceNiceScale: false,
      title: {
        text:    "SOH (%)",
        rotate:  -90,
        offsetX: 4,
        style:   { fontSize: "11px", fontWeight: 400, color: "#8F8F8F" },
      },
      labels: {
        formatter: (val) => Math.round(val),
        offsetX:   -8,
        style:     { fontSize: "11px", colors: "#8F8F8F" },
      },
    },
    grid: {
      borderColor: "#F0F0F0",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      padding: { left: 14, right: 8 },
    },
    markers: scatterMarkers,
    tooltip: { enabled: false },
    states: {
      hover:  { filter: { type: "darken", value: 0.35 } },
      active: { filter: { type: "none" } },
    },
  }), [
    chartId, xAxis, yAxis, xTickAmount, yTickAmount, xAxisTitle, activeXFilter,
    scatterColors, scatterMarkers, handleScatterMouseEnter, handleScatterMouseLeave, handleScatterDotClick,
  ]);

  const activeXLabel = xFilters.find((f) => f.key === activeXFilter)?.label || xAxisTitle;

  return (
    <Box sx={{ width: "100%" }}>

      {/* ── Legend ──────────────────────────────────────────────────────────── */}
      {showLegend && (
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "16px", mb: "8px" }}>
          {sohBuckets.map((cat) => {
            const isSelected   = selectedCategories.has(cat.name);
            const hasSelection = selectedCategories.size > 0;
            const isFaded      = hasSelection && !isSelected;
            return (
              <Box
                key={cat.name}
                data-testid={cat.isUnavailable ? "soh-scatter-legend-unavailable" : `soh-scatter-legend-${cat.name}`}
                onClick={() => onCategorySelect(cat.name)}
                sx={{
                  display: "flex", alignItems: "center", gap: "5px",
                  cursor: "pointer", opacity: isFaded ? 0.35 : 1,
                  transition: "opacity 0.18s", userSelect: "none",
                  "&:hover": { opacity: isFaded ? 0.55 : 0.78 },
                }}
              >
                <Box sx={getLegendSwatchSx(cat, isFaded)} />
                <Typography sx={{ fontSize: "12px", color: "#444B51", whiteSpace: "nowrap", fontWeight: isSelected ? 600 : 400 }}>
                  {buildLegendLabel(cat, legendUnavailableCount)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Chart ───────────────────────────────────────────────────────────── */}
      <Box sx={{ "& .apexcharts-series path, & .apexcharts-series circle": { cursor: "pointer" } }}>
        <ReactApexChart
          type="scatter"
          series={scatterSeries}
          options={scatterOptions}
          height={height}
        />
      </Box>

      {/* ── X-axis filter pills ──────────────────────────────────────────────── */}
      {xFilters.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: "4px", mt: "4px" }}>
          {xFilters.map((filter) => {
            const isActive = activeXFilter === filter.key;
            return (
              <Box
                key={filter.key}
                onClick={() => onXFilterChange(filter.key)}
                sx={{
                  px: "12px", py: "4px", borderRadius: "4px", cursor: "pointer",
                  border:  isActive ? "1px solid #6D72F6" : "1px solid transparent",
                  bgcolor: isActive ? "#EEF0FF" : "transparent",
                  transition: "all 0.15s", userSelect: "none",
                  "&:hover": { bgcolor: isActive ? "#EEF0FF" : "#F5F5F5" },
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: isActive ? 600 : 400, color: isActive ? "#6D72F6" : "#6B7280" }}>
                  {filter.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Hover tooltip ───────────────────────────────────────────────────── */}
      {scatterHover && (() => {
        const { battery, clientX, clientY, isUnavailable } = scatterHover;
        const { left, top } = smartTooltipPos(clientX, clientY, 220, 170);
        const resolvedFields = tooltipFields && tooltipFields.length > 0
          ? tooltipFields
          : ["soh", "age", "bmsCycles", ...(isVehicle ? ["odometer"] : [])];
        // Unavailable dots have no SOH: show only the x value, then a note.
        const xRenderer = TOOLTIP_FIELD_RENDERERS[activeXFilter];
        const detailRows = isUnavailable
          ? (xRenderer ? [[activeXLabel, xRenderer.render(battery)]] : [])
          : resolvedFields
          .map((field) => {
            const key = typeof field === "object" ? field.key : field;
            const renderer = TOOLTIP_FIELD_RENDERERS[key];
            if (!renderer) return null;
            const label = (typeof field === "object" && field.label) ? field.label : renderer.label;
            return [label, renderer.render(battery)];
          })
          .filter(Boolean);
        return (
          <Box
            onMouseEnter={() => clearTimeout(scatterHideTimerRef.current)}
            onMouseLeave={() => setScatterHover(null)}
            sx={{
              position: "fixed", left, top, zIndex: 9999,
              bgcolor: "#fff", border: "1px solid #E6E6E6", borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)", p: "12px 16px",
              minWidth: "220px", pointerEvents: "auto",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", mb: "8px" }}>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", flexShrink: 0 }}>
                {tooltipIdentifier?.label ?? (isVehicle ? "Bus Number" : "Serial Number")}
              </Typography>
              <TextWithCopy
                text={
                  tooltipIdentifier?.key
                    ? (battery[tooltipIdentifier.key] ?? "")
                    : (isVehicle ? (battery.licensePlateNo ?? "") : (battery.deviceID ?? ""))
                }
                type={tooltipIdentifier?.label ?? (isVehicle ? "Bus Number" : "Serial Number")}
                textStyle={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}
                iconButtonStyle={{ p: "5px", color: "#9E9E9E" }}
              />
            </Box>
            <Divider sx={{ mb: "8px", borderColor: "#F0F0F0" }} />
            {detailRows.map(([label, value]) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", mb: "5px" }}>
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{label}</Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>{value}</Typography>
              </Box>
            ))}
            {isUnavailable && (
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mt: "2px" }}>
                SOH not available
              </Typography>
            )}
          </Box>
        );
      })()}

    </Box>
  );
};

export default SOHScatterChart;
